// Payment display helpers: status chip config, receipt rows (§10.2) and the
// copied-receipt text format.
import type { Payment } from '@/types/phase2.types';
import { formatIndianCurrency } from '@/utils/currency';
import { RAIL_LABELS } from '@/utils/payments';
import { formatLong } from '@/mock/clock';

export interface ReceiptRow {
  label: string;
  value: string;
}

const statusText = (status: Payment['status']): string =>
  status === 'SUCCESS' ? 'Success' : status === 'PENDING' ? 'Processing' : 'Failed';

export const paymentStatusText = statusText;

const formatFx = (payment: Payment): { theyReceive?: string; rate?: string } => {
  if (!payment.fx) return {};
  const { currency, ratePaise, theyReceive } = payment.fx;
  const amount = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(theyReceive);
  const rate = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(ratePaise / 100);
  return { theyReceive: `${currency} ${amount}`, rate: `${rate} per ${currency}` };
};

/** Receipt rows in §10.2 order, each shown only when it has a value. */
export const receiptRows = (payment: Payment): ReceiptRow[] => {
  const rows: ReceiptRow[] = [{ label: 'Amount', value: formatIndianCurrency(payment.amountRupees) }];
  if (payment.chargesRupees > 0) {
    rows.push({ label: 'Charges', value: formatIndianCurrency(payment.chargesRupees) });
    rows.push({
      label: 'Total debited',
      value: formatIndianCurrency(payment.amountRupees + payment.chargesRupees),
    });
  }
  rows.push({ label: 'To', value: payment.payeeName });
  if (payment.beneficiaryAlias) rows.push({ label: 'Beneficiary', value: payment.beneficiaryAlias });
  rows.push({ label: 'Destination', value: payment.destination });
  rows.push({ label: 'Mode', value: RAIL_LABELS[payment.rail] });
  const fx = formatFx(payment);
  if (fx.theyReceive) rows.push({ label: 'They receive', value: fx.theyReceive });
  if (fx.rate) rows.push({ label: 'Rate', value: fx.rate });
  if (payment.purpose) rows.push({ label: 'Purpose', value: payment.purpose });
  rows.push({ label: 'Reference', value: payment.reference });
  rows.push({ label: 'Date', value: formatLong(payment.createdAt) });
  if (payment.remark) rows.push({ label: 'Remark', value: payment.remark });
  if (payment.failureReason) rows.push({ label: 'Reason', value: payment.failureReason });
  return rows;
};

/** Copied text (§10.2): first line "NovaBank payment · <status>", then
 *  one "Label: value" line per row. */
export const receiptCopyText = (payment: Payment): string =>
  [
    `NovaBank payment · ${statusText(payment.status)}`,
    ...receiptRows(payment).map((r) => `${r.label}: ${r.value}`),
  ].join('\n');

/** Where Repeat takes the customer (§10.1): Send money for a beneficiary,
 *  Pay via UPI for a direct UPI payment. */
export const repeatPath = (payment: Payment): string => {
  if (payment.beneficiaryId) {
    return `/pay/send?beneficiaryId=${payment.beneficiaryId}&accountId=${payment.accountId ?? ''}`;
  }
  return `/pay/upi?to=${encodeURIComponent(payment.destination)}`;
};
